<template>
  <f7-popup
    v-model:opened="opened"
    class="pc-part-add-article-popup hide-popup-scanner"
  >
    <!-- Content of the popup -->
    <f7-page v-if="opened">
      <template #fixed>
        <TheNavbar
          nav-title="Añadir artículo"
          class="secondary-navbar"
          :popup-close-btn-enable="true"
        />
      </template>

      <f7-block>
        <UCSearchEntityERP
          ref="searchArticleRef"
          title="Búsqueda de artículos"
          :placeholder="$t('searchEntity.article.placeholder.searchByNameOrCode')"
          :search-text="$t('searchEntity.article.searchText')"
          :search-cancellation-text="$t('searchEntity.article.searchCancellationText')"
          :search-not-found-text="$t('searchEntity.article.searchNotFoundText')"
          :end-point-for-code-search="fetchArticles"
          :end-point-for-list="fetchArticles"
          :property-entity-shown-input="['Articulo.Codigo', 'Articulo.Nombre']"
          :property-entity-shown-list-title="['Articulo.Codigo']"
          :property-entity-shown-list-subtitle="['Articulo.Nombre', 'Almacen', 'Cantidad', 'Articulo.TipoVarios']"
          :min-text-search="1"
          :autofocus-on-deselection="true"
          :auto-focus-on-empty-search-result="true"
          :qr-search-show="true"
          :qr-search-params="{
            showInputButton: true,
            fabButtonShow: false,
            allowManualCodeEntry: true,
            autoOpenCamera: false,
            autofocusOnInput: true,
          }"
          :dont-qr-select="true"
          :disabled="isSavingArticle"
          @qr-value="fetchArticleByBarcode"
          @selection="onArticleSelection"
        >
          <template #subtitle-1="{ item }">
            <span class="accumulated-stock-info">
              {{ `${$t('common.warehouse')}: ${item.Codigo}/${item.Nombre}` }}
            </span>
          </template>
          <template #subtitle-2="{ item }">
            <span class="accumulated-stock-info">{{ `${$t('common.stocks')}: ${item}` }}</span>
          </template>
          <template #subtitle-3="{ item }">
            <span class="accumulated-stock-info">Vario: {{ item ? 'Sí' : 'No' }}</span>
          </template>
        </UCSearchEntityERP>
      </f7-block>

      <f7-block v-if="selectedArticle?.Articulo">
        <div class="container-data">
          <label>
            {{ $t('common.quantity') }}
          </label>
          <div class="display-flex">
            <f7-stepper
              fill
              :manual-input-mode="true"
              :min="10 ** -(selectedArticle.Articulo.PrecisionCantidades)"
              :max="selectedArticle.Cantidad"
              :value="initialAmountInputValue"
              :decimal-point="selectedArticle.Articulo.PrecisionCantidades"
              :step="10 ** -(selectedArticle.Articulo.PrecisionCantidades)"
              :disabled="isSavingArticle"
              :format-value="formatInputValue"
              @stepper:change="amount = formatInputValue($event)"
            />
          </div>
        </div>

        <template v-if="selectedArticle.Articulo?.TipoVarios">
          <div class="container-data margin-top margin-bottom">
            <label class="margin-left-half">
              {{ $t('common.price') }}
            </label>
            <input
              v-model="price"
              class="padding-left-half padding-right-half"
              type="number"
            >
          </div>
          <div class="container-data">
            <label class="margin-left-half">
              Descripción
            </label>
            <textarea
              v-model="observations"
              class="padding-half"
            />
          </div>
        </template>
      </f7-block>

      <!-- Save Button -->
      <f7-block>
        <f7-button
          :preloader="isSavingArticle"
          :loading="isSavingArticle"
          :disabled="!isSaveArticleEnabled || isSavingArticle"
          fill
          color="green"
          @click="handleSaveArticle"
        >
          <UCSvgIcon
            name="fa6-solid-floppy-disk"
            class="margin-right-half"
          />
          <span class="no-margin">
            Guardar artículo
          </span>
        </f7-button>
      </f7-block>
    </f7-page>
  </f7-popup>
</template>

<script setup>
// @ts-check
import {
  computed,
  ref,
  shallowRef,
  watch,
} from 'vue';
import { HiDialog, HiNotification } from '@/js/common/HINotificationSupport';
import { callFetchAccumulatedStock, callSaveArticles } from '@/js/services/repairs/ApiRepairs';
import { logError } from '@/js/utils/log';

import TheNavbar from '@/components/TheNavbar.vue';
import UCSearchEntityERP from '@/components/common/UCSearchEntityERP.vue';
import UCSvgIcon from '@/components/common/UCSvgIcon.vue';

/** @import { Ref, ShallowRef } from 'vue' */
/**
 * @import {
 * AcumuladoExistencias,
 * DatosParteReparacion,
 * } from '@erpagroapp/types' */

const props = defineProps({
  workPartId: { type: Number, default: undefined },
});

const emit = defineEmits(['success']);
const opened = defineModel('opened', { type: Boolean, default: false });

/** @type {Ref<string>} */
const amount = ref('');
/** @type {Ref<number | undefined>} */
const initialAmountInputValue = ref(undefined);
/** @type {Ref<string>} */
const price = ref('');
/** @type {Ref<string>} */
const observations = ref('');
/**
 * Stores the selected article (as `AcumuladoExistencias` object)
 * @type {Ref<AcumuladoExistencias | null>}
 */
const selectedArticle = ref(null);

function clearArticleData() {
  amount.value = '';
  initialAmountInputValue.value = undefined;
  price.value = '';
  observations.value = '';
}

watch(opened, () => {
  if (!opened.value) {
    clearArticleData();
  }
});

/**
 * Updates the amount input.
 * @param {string} value - The current value.
 * @returns {string} The new value.
 */
function formatInputValue(value) {
  const precision = selectedArticle.value?.Articulo?.PrecisionCantidades ?? 0;
  if (value === '') {
    return '';
  }

  const numericValue = Number(value);
  return Number.isInteger(numericValue) ? numericValue.toString() : numericValue.toFixed(precision);
}

function useArticleSelection() {
  /** @type {ShallowRef<InstanceType<typeof UCSearchEntityERP> | null>} */
  const searchArticleRef = shallowRef(null);

  /**
   * A method that is called when user searches by value.
   * @param {string | null} value - The search value.
   */
  async function fetchArticles(value) {
    try {
      return await callFetchAccumulatedStock({
        filter: value ? `q:${value}` : '',
      });
    } catch (error) {
      logError(error);
      return null;
    }
  }

  /**
   * A method that is called when user searches by value.
   * @param {string | null} value - The search value.
   */
  async function fetchArticleByBarcode(value) {
    HiDialog.Preloader(true, 'Buscando artículo por código de barras...');

    try {
      const { data } = await callFetchAccumulatedStock({
        filter: `barcode:${value}`,
      });

      if (data.length > 0) {
        searchArticleRef.value?.SelectEntity(data[0], true);
      }
    } catch (error) {
      logError(error);
    } finally {
      HiDialog.Preloader(false);
    }
  }

  /**
   * A method that is called when user selects an article in the popup.
   * @param {AcumuladoExistencias | null} article - The selected article.
   * @returns {void}
   */
  function onArticleSelection(article) {
    selectedArticle.value = article;

    if (!article) {
      clearArticleData();
    } else {
      initialAmountInputValue.value = article.Cantidad < 1 ? article.Cantidad : 1;
      amount.value = initialAmountInputValue.value.toString();
    }
  }

  watch(opened, () => {
    if (!opened.value) {
      if (selectedArticle.value) {
        selectedArticle.value = null;
        searchArticleRef.value?.Clear();
      }
    }
  });

  return {
    searchArticleRef,
    fetchArticles,
    fetchArticleByBarcode,
    onArticleSelection,
  };
}

function useSaveArticle() {
  /** Controls whether a preloader on the button is shown when saving the article. */
  const isSavingArticle = ref(false);

  /** Controls whether the 'Save' button should be enabled. */
  const isSaveArticleEnabled = computed(() => {
    if (!selectedArticle.value) {
      return false;
    }

    if (selectedArticle.value.Articulo?.TipoVarios) {
      if (price.value.trim().length === 0) {
        return false;
      }
      if (observations.value.trim().length === 0) {
        return false;
      }
    }

    return true;
  });

  /**
   * A method that is used to save the article
   * @param {PartialDeep<DatosParteReparacion>} repairPartData - The repair part data.
   * @returns {Promise<boolean>} `true` if the operation was successful, `false` otherwise.
   */
  async function saveArticle(repairPartData) {
    let result = false;

    try {
      isSavingArticle.value = true;
      const { data } = await callSaveArticles(repairPartData);

      if (data.Valor === true) {
        result = true;
        HiNotification.Toast('El artículo se ha guardado correctamente');
      } else {
        HiNotification.Toast(data.Mensaje || 'No se ha podido guardar el artículo');
      }
    } catch (error) {
      logError(error);
      HiNotification.Toast('No se ha podido guardar el artículo');
    } finally {
      isSavingArticle.value = false;
    }

    return result;
  }

  /**
   * Handles and prepares the saving of the article when the 'Save' button is clicked.
   * @returns {Promise<void>}
   */
  async function handleSaveArticle() {
    if (!props.workPartId) {
      return;
    }

    if (!selectedArticle.value || !isSaveArticleEnabled.value) {
      return;
    }

    /** @type {PartialDeep<DatosParteReparacion>} */
    const repairPartData = {
      ParteProduccion: {
        Id: props.workPartId,
        ParteProduccionReal: {
          DatosEntrada: [
            {
              Almacen: {
                Id: selectedArticle.value.Almacen?.Id,
              },
              Articulo: selectedArticle.value.Articulo,
              Cantidad: Number(amount.value),
              Observaciones: observations.value || '',
              Precio: price.value.length > 0 ? Number(price.value) : 0.0,
            },
          ],
        },
      },
    };

    const result = await saveArticle(repairPartData);
    if (result) {
      opened.value = false;
      emit('success', props.workPartId);
    }
  }

  return {
    isSavingArticle,
    isSaveArticleEnabled,
    handleSaveArticle,
  };
}

const {
  searchArticleRef,
  fetchArticles,
  fetchArticleByBarcode,
  onArticleSelection,
} = useArticleSelection();

const {
  isSavingArticle,
  isSaveArticleEnabled,
  handleSaveArticle,
} = useSaveArticle();
</script>

<style scoped lang="scss">
.pc-part-add-article-popup {
  --f7-popup-tablet-height: 300px;

  .container-data {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 4px;

    input {
      background: #fff;
      border: 1px solid var(--his-silver);
      border-radius: 8px;
      display: flex;
      align-items: center;
      width: 100%;
      height: 36px;
      color: #24253d;
      font-size: 14px;

      &.input-with-value {
        color: #000;
      }
    }

    textarea {
      width: 100%;
      min-height: 82px;
      height: 82px;
      resize: vertical;
      border: 1px solid var(--his-silver);
      border-radius: 8px;
      background-color: #fff;
      padding-top: calc(var(--f7-typography-padding) / 2);
      padding-left: calc(var(--f7-typography-padding) / 2);
    }
  }
}

.Search-popup {
  #search-entity-page-content {
    .accumulated-stock-info {
      font-size: 12px;
    }
  }
}
</style>
